
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { DevelopmentRecipe } from '@/types/develop';
import { cn } from '@/lib/utils';
import { Play, Edit, Trash2, Heart } from 'lucide-react';

interface MobileRecipeCardProps {
  recipe: DevelopmentRecipe;
  onStart: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
  darkMode?: boolean;
}

export const MobileRecipeCard: React.FC<MobileRecipeCardProps> = ({
  recipe,
  onStart,
  onEdit,
  onDelete,
  onToggleFavorite,
  darkMode = false,
}) => {
  return (
    <Card className={cn(
      'w-full overflow-hidden border',
      darkMode && 'bg-black border-red-900'
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h3 className={cn(
              'font-semibold text-lg',
              darkMode && 'text-red-400'
            )}>
              {recipe.name}
            </h3>
            
            {recipe.filmStock && (
              <p className={cn(
                'text-sm text-muted-foreground',
                darkMode && 'text-red-700'
              )}>
                Film: {recipe.filmStock}
                {recipe.iso && ` @ ${recipe.iso} ISO`}
                {recipe.pushPull !== 'standard' && recipe.pushPullStops && 
                  ` (${recipe.pushPull === 'push' ? '+' : '-'}${recipe.pushPullStops})`
                }
              </p>
            )}
            
            {recipe.developer && (
              <p className={cn(
                'text-sm text-muted-foreground',
                darkMode && 'text-red-700'
              )}>
                Developer: {recipe.developer}
              </p>
            )}
            
            <p className={cn(
              'text-sm text-muted-foreground',
              darkMode && 'text-red-700'
            )}>
              {recipe.temperature && `${recipe.temperature}°C | `}
              {recipe.steps.length} steps | 
              {Math.floor(recipe.steps.reduce((acc, step) => acc + step.time, 0) / 60)}:
              {String(recipe.steps.reduce((acc, step) => acc + step.time, 0) % 60).padStart(2, '0')}
            </p>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleFavorite}
            className={cn(
              recipe.isFavorite ? 'text-rose-500' : 'text-muted-foreground',
              darkMode && !recipe.isFavorite && 'text-red-700',
              darkMode && 'hover:bg-red-950'
            )}
          >
            <Heart
              className={cn(
                'h-5 w-5',
                recipe.isFavorite && 'fill-rose-500'
              )}
            />
          </Button>
        </div>
      </CardContent>
      
      <CardFooter className="p-4 pt-0 flex justify-between gap-2">
        <Button
          variant="default"
          className={cn(
            'flex-1',
            darkMode && 'bg-red-800 hover:bg-red-700 text-white'
          )}
          onClick={onStart}
        >
          <Play className="h-4 w-4 mr-1" />
          Start
        </Button>
        
        <Button
          variant="outline"
          className={cn(
            'flex-1',
            darkMode && 'border-red-900 text-red-500 hover:bg-red-950'
          )}
          onClick={onEdit}
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
        
        <Button
          variant="outline"
          className={cn(
            'flex-none',
            darkMode && 'border-red-900 text-red-500 hover:bg-red-950'
          )}
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};
